import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './MiniFeedEventos.css';
import databaseService from '../utils/database';

const eventosOriginales = [
  {
    tipo: 'NETWORKING',
    titulo: 'SUNSET BUSINESS NETWORKING',
    fechaISO: '2025-07-31T18:00:00',
  },
  {
    tipo: 'BOOTCAMP',
    titulo: 'BOOTCAMP VIBES',
    fechaISO: '2025-07-26T11:00:00',
  },
  {
    tipo: 'INTERNATIONAL',
    titulo: 'BACK 2 BUSINESS INTERNATIONAL',
    fechaISO: '2025-09-11T18:00:00',
  }
];

const bgClasses = ['mini-feed-gold','mini-feed-blue','mini-feed-violet'];

// Función para formatear fecha legible
function formatearFecha(fechaISO) {
  const fecha = new Date(fechaISO);
  const meses = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];
  
  const dia = fecha.getDate();
  const mes = meses[fecha.getMonth()];
  const año = fecha.getFullYear();
  
  return `${dia}/${mes}/${año}`;
}

// Función para formatear hora legible
function formatearHora(fechaISO) {
  const fecha = new Date(fechaISO);
  const horas = fecha.getHours().toString().padStart(2, '0');
  const minutos = fecha.getMinutes().toString().padStart(2, '0');
  
  return `${horas}:${minutos} Hrs`;
}

function ordenarEventosPorProximidad(eventos) {
  const ahora = new Date();
  
  // Filtrar eventos que ya expiraron (más de 6 horas después)
  const eventosValidos = eventos.filter(evento => {
    const fechaEvento = new Date(evento.fechaISO);
    const diffHoras = (fechaEvento - ahora) / (1000 * 60 * 60);
    // Solo incluir si está en curso, es futuro, o finalizó hace menos de 6 horas
    return diffHoras > -6;
  });

  return eventosValidos.sort((a, b) => {
    const estadoA = getEstadoEvento(a.fechaISO);
    const estadoB = getEstadoEvento(b.fechaISO);
    const fechaA = new Date(a.fechaISO);
    const fechaB = new Date(b.fechaISO);

    // 1. En curso primero
    if (estadoA.tipo === 'curso' && estadoB.tipo !== 'curso') return -1;
    if (estadoB.tipo === 'curso' && estadoA.tipo !== 'curso') return 1;

    // 2. Futuros por proximidad
    if (fechaA >= ahora && fechaB >= ahora) {
      return fechaA - fechaB;
    }

    // 3. Si solo uno ya pasó, mostrar primero el que no ha pasado
    if (fechaA < ahora && fechaB >= ahora) return 1;
    if (fechaB < ahora && fechaA >= ahora) return -1;

    // 4. Ambos finalizados: más reciente primero
    if (fechaA < ahora && fechaB < ahora) {
      return fechaB - fechaA;
    }

    return 0;
  });
}

function getEstadoEvento(fechaISO) {
  const ahora = new Date();
  const evento = new Date(fechaISO);
  const diffMs = evento - ahora;
  const diffHoras = diffMs / (1000 * 60 * 60);
  const diffDias = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  // Si el evento ya pasó pero está dentro de las 6 horas posteriores
  if (diffMs < 0 && diffHoras > -6) {
    return { texto: 'En curso', tipo: 'curso' };
  }
  // Si el evento ya pasó hace más de 6 horas
  if (diffHoras <= -6) {
    return { texto: 'Finalizado', tipo: 'finalizado' };
  }
  // Si faltan menos de 24 horas
  if (diffHoras < 24 && diffHoras > 0) {
    const horas = Math.ceil(diffHoras);
    return { texto: `Faltan ${horas} hora${horas === 1 ? '' : 's'}`, tipo: 'horas' };
  }
  // Si faltan días
  if (diffDias >= 1) {
    return { texto: `Faltan ${diffDias} día${diffDias === 1 ? '' : 's'}`, tipo: 'dias' };
  }
  // Si faltan menos de 1 día pero más de 0 horas
  if (diffDias < 1 && diffHoras > 0) {
    const horas = Math.ceil(diffHoras);
    return { texto: `Faltan ${horas} hora${horas === 1 ? '' : 's'}`, tipo: 'horas' };
  }
  return { texto: '', tipo: '' };
}

export default function MiniFeedEventos() {
  const navigate = useNavigate();
  const [eventos, setEventos] = useState([]);
  const [index, setIndex] = useState(0);
  const [estado, setEstado] = useState({ texto: '', tipo: '' });
  const [lastUpdate, setLastUpdate] = useState(Date.now());

  // Función para actualizar completamente los datos
  const actualizarDatosCompletos = useCallback(async () => {
    try {
      // Intentar cargar desde backend
      const resp = await databaseService.getEvents();
      let lista = Array.isArray(resp?.events) ? resp.events : [];
      if (!lista.length) {
        // Fallback a los eventos mock si backend vacío
        lista = eventosOriginales;
      }
      
      // Convertir campo 'fecha' a 'fechaISO' para compatibilidad
      const eventosNormalizados = lista.map(evento => ({
        ...evento,
        fechaISO: evento.fecha || evento.fechaISO
      }));
      
      const eventosOrdenados = ordenarEventosPorProximidad(eventosNormalizados);
      setEventos(eventosOrdenados);
      setLastUpdate(Date.now());
      if (eventosOrdenados.length > 0) {
        const nuevoEstado = getEstadoEvento(eventosOrdenados[0]?.fechaISO || '');
        setEstado(nuevoEstado);
        setIndex(0);
      }
    } catch (e) {
      // Fallback silencioso a mock
      const eventosOrdenados = ordenarEventosPorProximidad(eventosOriginales);
      setEventos(eventosOrdenados);
      setLastUpdate(Date.now());
      if (eventosOrdenados.length > 0) {
        const nuevoEstado = getEstadoEvento(eventosOrdenados[0]?.fechaISO || '');
        setEstado(nuevoEstado);
        setIndex(0);
      }
    }
  }, []);

  // Forzar recarga completa al montar el componente
  useEffect(() => {
    actualizarDatosCompletos();
  }, [actualizarDatosCompletos]);

  // Actualizar datos cada 30 segundos para evitar caché
  useEffect(() => {
    const actualizarTimer = setInterval(() => {
      actualizarDatosCompletos();
    }, 30000); // Cada 30 segundos

    return () => clearInterval(actualizarTimer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Actualizar estado del evento actual cada minuto
  useEffect(() => {
    const estadoTimer = setInterval(() => {
      if (eventos.length > 0 && eventos[index]) {
        const nuevoEstado = getEstadoEvento(eventos[index].fechaISO);
        setEstado(nuevoEstado);
      }
    }, 60000); // Cada minuto

    return () => clearInterval(estadoTimer);
  }, [index, eventos]);

  // Rotación automática de eventos
  useEffect(() => {
    const rotationTimer = setInterval(() => {
      setIndex((prev) => (prev + 1) % eventos.length);
    }, 6000);

    return () => clearInterval(rotationTimer);
  }, [eventos.length]);

  // Actualizar estado inmediatamente al cambiar de evento
  useEffect(() => {
    if (eventos.length > 0 && eventos[index]) {
      const nuevoEstado = getEstadoEvento(eventos[index].fechaISO);
      setEstado(nuevoEstado);
    }
  }, [index, eventos]);

  // Reordenar eventos cada 5 minutos para mantener orden actualizado
  useEffect(() => {
    const reordenarTimer = setInterval(async () => {
      await actualizarDatosCompletos();
    }, 300000); // Cada 5 minutos
    return () => clearInterval(reordenarTimer);
  }, [actualizarDatosCompletos]);

  const evento = eventos[index];
  const bgClass = bgClasses[index % 3];

  if (!evento) return null;

  return (
    <div className={`mini-feed-eventos ${bgClass}`} key={lastUpdate}>
      <div className="mini-feed-badges-row-fixed">
        <div className="mini-feed-badges-group">
          <span className="mini-badge tipo">{evento.tipo}</span>
          {estado.texto && estado.tipo !== 'finalizado' && (
            <span className={`mini-badge countdown ${estado.tipo === 'horas' ? 'countdown-alerta' : estado.tipo === 'curso' ? 'countdown-activo' : 'countdown-normal'}`}>
              {estado.texto}
            </span>
          )}
          {estado.tipo === 'finalizado' && (
            <span className="mini-badge countdown countdown-finalizado">Finalizado</span>
          )}
        </div>
        <div className="mini-feed-indicators-inline">
          {eventos.map((_, i) => (
            <span
              key={i}
              className={i === index ? 'mini-feed-dot active' : 'mini-feed-dot'}
              onClick={() => setIndex(i)}
            />
          ))}
        </div>
      </div>
      <div className="mini-feed-main-row">
        <div className="mini-feed-info">
          <div className="mini-feed-titulo">{evento.titulo}</div>
          <div className="mini-feed-fecha">
            {formatearFecha(evento.fechaISO)} <span className="separator">•</span> {formatearHora(evento.fechaISO)}
          </div>
        </div>
        <button 
          className="mini-badge reservar"
          onClick={() => {
            console.log('🔘 Click en RESERVAR. Evento:', evento);
            if (evento && evento.id) {
              console.log('✅ Navegando a evento ID:', evento.id);
              navigate(`/evento/${evento.id}`);
            } else {
              console.error('❌ Evento no tiene ID válido:', evento);
              alert('Error: No se pudo identificar el evento. Por favor, recarga la página.');
            }
          }}
        >
          RESERVAR
          <svg className="mini-arrow" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>
    </div>
  );
} 